import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Users, Search, Phone, Mail, Copy, ExternalLink, ChevronLeft, ChevronRight,
  CheckCircle2, AlertCircle, Clock, Loader2, RefreshCw, Filter,
  TrendingDown, UserCheck, Building2, CreditCard, Package, BarChart3,
  Cloud, CloudOff, Upload, Download, KeyRound, Store, ShieldCheck,
  ShieldAlert, ListChecks, Smartphone, Globe
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;

// ─── New 9-stage funnel configuration ───────────────────────────────────────
// Stage IDs match the backend current_stage values
const STAGE_CONFIG = {
  // Signup stages — shown together as "any 1 is sufficient"
  signup_incomplete: {
    label: 'Signup Incomplete',
    description: 'Not verified via any signup method',
    color: 'bg-red-100 text-red-800 border-red-200',
    icon: AlertCircle,
    step: 1,
  },
  // ↳ once any signup method is done, vendor moves to next stage
  account_password: {
    label: 'Account with Password',
    description: 'Signed up but password not set',
    color: 'bg-orange-100 text-orange-800 border-orange-200',
    icon: KeyRound,
    step: 2,
  },
  store_identity: {
    label: 'Store Identity',
    description: 'Store name & identity not configured',
    color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    icon: Store,
    step: 3,
  },
  business_info: {
    label: 'Business Info',
    description: 'Business & tax information incomplete',
    color: 'bg-amber-100 text-amber-800 border-amber-200',
    icon: Building2,
    step: 4,
  },
  bank_details: {
    label: 'Bank Details',
    description: 'Bank account not added',
    color: 'bg-blue-100 text-blue-800 border-blue-200',
    icon: CreditCard,
    step: 5,
  },
  plan_selection: {
    label: 'Plan Selection',
    description: 'Subscription plan not selected',
    color: 'bg-purple-100 text-purple-800 border-purple-200',
    icon: ListChecks,
    step: 6,
  },
  kyc_pending: {
    label: 'KYC Pending',
    description: 'KYC submitted – awaiting verification',
    color: 'bg-indigo-100 text-indigo-800 border-indigo-200',
    icon: ShieldAlert,
    step: 7,
  },
  kyc_completed: {
    label: 'KYC Completed',
    description: 'Verified – no listing published yet',
    color: 'bg-teal-100 text-teal-800 border-teal-200',
    icon: ShieldCheck,
    step: 8,
  },
  listing_complete: {
    label: '1st Listing Completed',
    description: 'Active vendor with published listings',
    color: 'bg-green-100 text-green-800 border-green-200',
    icon: CheckCircle2,
    step: 9,
  },
};

// Summary card configuration (funnel metrics)
const SUMMARY_CARDS = [
  // Signup breakdown — shown as a group (any 1 = done)
  { key: 'google_signup',    label: 'Google Signup',   icon: Globe,        color: 'text-red-500',     group: 'signup' },
  { key: 'mobile_signup',    label: 'Mobile Signup',   icon: Smartphone,   color: 'text-green-600',   group: 'signup' },
  { key: 'email_signup',     label: 'Email Signup',    icon: Mail,         color: 'text-blue-600',    group: 'signup' },
  // Funnel progression
  { key: 'account_password', label: 'With Password',   icon: KeyRound,     color: 'text-orange-600',  group: 'funnel' },
  { key: 'store_identity',   label: 'Store Identity',  icon: Store,        color: 'text-yellow-600',  group: 'funnel' },
  { key: 'business_info',    label: 'Business Info',   icon: Building2,    color: 'text-amber-600',   group: 'funnel' },
  { key: 'bank_details',     label: 'Bank Details',    icon: CreditCard,   color: 'text-blue-600',    group: 'funnel' },
  { key: 'plan_selection',   label: 'Plan Selected',   icon: ListChecks,   color: 'text-purple-600',  group: 'funnel' },
  { key: 'kyc_completed',    label: 'KYC Completed',   icon: ShieldCheck,  color: 'text-teal-600',    group: 'funnel' },
  { key: 'listing_complete', label: '1st Listing',     icon: Package,      color: 'text-green-600',   group: 'funnel' },
];

const VendorOnboardingFunnel = () => {
  const { apiCall, isSuperAdmin } = useAuth();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [vendors, setVendors] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, total_pages: 1, total_count: 0 });
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const [searchDebounce, setSearchDebounce] = useState('');

  // Zoho CRM state
  const [crmStatus, setCrmStatus] = useState(null);
  const [syncingVendor, setSyncingVendor] = useState(null);
  const [bulkSyncing, setBulkSyncing] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setSearchDebounce(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    fetchFunnelData();
    fetchSummary();
  }, [pagination.page, searchDebounce, stageFilter]);

  useEffect(() => {
    fetchCrmStatus();
  }, []);

  const fetchCrmStatus = async () => {
    try {
      const data = await apiCall('/api/zoho-crm/status');
      setCrmStatus(data);
    } catch {
      setCrmStatus({ is_connected: false });
    }
  };

  const syncVendorToCrm = async (vendorId) => {
    setSyncingVendor(vendorId);
    try {
      const result = await apiCall(`/api/zoho-crm/sync/from-funnel/${vendorId}`, { method: 'POST' });
      if (result.success) {
        toast({ title: 'Synced to Zoho CRM', description: 'Vendor lead created/updated.' });
        fetchCrmStatus();
      } else {
        toast({ title: 'Sync Failed', description: result.error || 'Failed to sync', variant: 'destructive' });
      }
    } catch {
      toast({ title: 'Error', description: 'Failed to sync vendor to Zoho CRM', variant: 'destructive' });
    } finally {
      setSyncingVendor(null);
    }
  };

  const bulkSyncToCrm = async () => {
    setBulkSyncing(true);
    try {
      const result = await apiCall('/api/zoho-crm/sync/bulk?limit=50', { method: 'POST' });
      toast({ title: 'Bulk Sync Complete', description: `Synced ${result.success}, ${result.failed} failed` });
      fetchCrmStatus();
    } catch {
      toast({ title: 'Error', description: 'Failed to run bulk sync', variant: 'destructive' });
    } finally {
      setBulkSyncing(false);
    }
  };

  const exportToCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (searchDebounce) params.append('search', searchDebounce);
      if (stageFilter && stageFilter !== 'all') params.append('stage_filter', stageFilter);
      params.append('page_size', '1000');

      const data = await apiCall(`/api/dashboard/admin/onboarding-funnel?${params}`);
      const exportVendors = data.vendors || [];

      if (exportVendors.length === 0) {
        toast({ title: 'No Data', description: 'No vendors to export', variant: 'destructive' });
        return;
      }

      const headers = [
        'Name', 'Email', 'Phone', 'Vendor ID',
        'Signup Method', 'Current Stage', 'Stage Description',
        'Has Password', 'Store Identity', 'Business Info (PAN)',
        'Bank Details', 'Plan Selected', 'KYC Status',
        'Onboarding Progress %', 'Published Listings',
        'Registration Date', 'Stage Details'
      ];

      const rows = exportVendors.map(v => [
        v.name || '',
        v.email || '',
        v.phone || '',
        v.vendor_id || '',
        v.auth_method || '',
        STAGE_CONFIG[v.current_stage]?.label || v.current_stage || '',
        STAGE_CONFIG[v.current_stage]?.description || '',
        v.has_password ? 'Yes' : 'No',
        v.initial_questions_answered ? 'Yes' : 'No',
        v.pan_provided ? 'Yes' : 'No',
        v.bank_details_provided ? 'Yes' : 'No',
        v.subscription_status || (v.subscription_plan ? 'Selected' : 'No'),
        v.is_onboarding_completed ? 'Completed' : 'Pending',
        v.onboarding_progress || 0,
        v.published_listings || 0,
        v.joined_at ? new Date(v.joined_at).toLocaleDateString('en-IN') : '',
        (v.stage_details || []).join('; ')
      ]);

      const escapeCSV = (val) => {
        const str = String(val);
        return (str.includes(',') || str.includes('"') || str.includes('\n'))
          ? `"${str.replace(/"/g, '""')}"` : str;
      };

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(escapeCSV).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `vendor_onboarding_funnel_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();

      toast({ title: 'Exported', description: `${exportVendors.length} vendors exported to CSV` });
    } catch (error) {
      toast({ title: 'Export Failed', description: error.message, variant: 'destructive' });
    }
  };

  const fetchFunnelData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: pagination.page, page_size: 50 });
      if (searchDebounce) params.append('search', searchDebounce);
      if (stageFilter && stageFilter !== 'all') params.append('stage_filter', stageFilter);
      const data = await apiCall(`/api/dashboard/admin/onboarding-funnel?${params}`);
      setVendors(data.vendors || []);
      setPagination(data.pagination || { page: 1, total_pages: 1, total_count: 0 });
    } catch {
      toast({ title: 'Error', description: 'Failed to load onboarding funnel data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const data = await apiCall('/api/dashboard/admin/onboarding-funnel/summary');
      setSummary(data);
    } catch (error) {
      console.error('Failed to load funnel summary:', error);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast({ title: 'Copied', description: 'Copied to clipboard' });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getStageConfig = (stage) => STAGE_CONFIG[stage] || STAGE_CONFIG.listing_complete;

  // Signup method label for vendor display
  const getSignupMethodBadge = (vendor) => {
    const method = vendor.auth_method || 'email';
    if (method === 'google') return <span className="text-xs text-red-600 font-medium flex items-center gap-0.5"><Globe className="h-3 w-3" />Google</span>;
    if (method === 'mobile') return <span className="text-xs text-green-600 font-medium flex items-center gap-0.5"><Smartphone className="h-3 w-3" />Mobile</span>;
    return <span className="text-xs text-blue-600 font-medium flex items-center gap-0.5"><Mail className="h-3 w-3" />Email</span>;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <BarChart3 className="h-6 w-6 text-indigo-600" />
              Vendor Onboarding Funnel
            </h1>
            <p className="text-muted-foreground text-sm">
              9-stage funnel — track vendor progress from signup to first listing
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {crmStatus && (
              <Badge variant={crmStatus.is_connected ? 'default' : 'secondary'} className="gap-1">
                {crmStatus.is_connected ? (
                  <><Cloud className="h-3 w-3" />CRM: {crmStatus.synced_vendors}/{crmStatus.total_vendors}</>
                ) : (
                  <><CloudOff className="h-3 w-3" />CRM Not Connected</>
                )}
              </Badge>
            )}
            {crmStatus?.is_connected && crmStatus.pending_sync > 0 && (
              <Button variant="outline" size="sm" onClick={bulkSyncToCrm} disabled={bulkSyncing} data-testid="bulk-sync-crm">
                {bulkSyncing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
                Sync {Math.min(50, crmStatus.pending_sync)} to CRM
              </Button>
            )}
            {/* Export CSV — Super Admin only */}
            {isSuperAdmin && (
              <Button variant="outline" size="sm" onClick={exportToCSV} data-testid="export-csv">
                <Download className="h-4 w-4 mr-1" />
                Export CSV
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => { fetchFunnelData(); fetchSummary(); }}>
              <RefreshCw className="h-4 w-4 mr-1" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Funnel Summary — two rows */}
        {summary && (
          <div className="space-y-3">
            {/* Signup Methods Row */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1">
                <Users className="h-3 w-3" />
                Signup Methods — any 1 method qualifies
              </p>
              <div className="grid grid-cols-3 gap-3">
                {SUMMARY_CARDS.filter(c => c.group === 'signup').map(({ key, label, icon: Icon, color }) => (
                  <Card key={key} className="text-center border-dashed">
                    <CardContent className="pt-3 pb-3">
                      <Icon className={`h-4 w-4 mx-auto mb-1 ${color}`} />
                      <div className="text-2xl font-bold">{summary.stages?.[key] ?? 0}</div>
                      <div className="text-xs text-muted-foreground">{label}</div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Funnel Progression Row */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1">
                <TrendingDown className="h-3 w-3" />
                Funnel Progression
              </p>
              <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3">
                {SUMMARY_CARDS.filter(c => c.group === 'funnel').map(({ key, label, icon: Icon, color }) => (
                  <Card key={key} className="text-center cursor-pointer hover:ring-2 hover:ring-indigo-300 transition-all"
                    onClick={() => { setStageFilter(key); setPagination(p => ({ ...p, page: 1 })); }}>
                    <CardContent className="pt-3 pb-3">
                      <Icon className={`h-4 w-4 mx-auto mb-1 ${color}`} />
                      <div className="text-xl font-bold">{summary.stages?.[key] ?? 0}</div>
                      <div className="text-xs text-muted-foreground leading-tight">{label}</div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Drop-off Alert */}
        {summary?.drop_off_points && Object.keys(summary.drop_off_points).length > 0 && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="py-3">
              <div className="flex items-center gap-2 text-amber-800 flex-wrap">
                <TrendingDown className="h-5 w-5 flex-shrink-0" />
                <span className="font-medium text-sm">Biggest Drop-offs:</span>
                {Object.entries(summary.drop_off_points)
                  .sort((a, b) => b[1].percentage - a[1].percentage)
                  .slice(0, 3)
                  .map(([key, value]) => (
                    <Badge key={key} variant="outline" className="bg-white text-xs">
                      {key.replace(/_to_/g, ' → ').replace(/_/g, ' ')}: {value.percentage}%
                    </Badge>
                  ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stage legend pill row */}
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-xs text-muted-foreground font-medium">Stage key:</span>
          {Object.entries(STAGE_CONFIG).map(([id, cfg]) => (
            <span
              key={id}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border cursor-pointer ${cfg.color} ${stageFilter === id ? 'ring-2 ring-offset-1 ring-indigo-500' : ''}`}
              onClick={() => setStageFilter(stageFilter === id ? 'all' : id)}
              title={`Click to filter: ${cfg.description}`}
            >
              <cfg.icon className="h-3 w-3" />
              {cfg.step}. {cfg.label}
            </span>
          ))}
          {stageFilter !== 'all' && (
            <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => setStageFilter('all')}>
              Clear filter ×
            </Button>
          )}
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="py-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, email, or phone..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="search-vendors"
                />
              </div>
              <Select value={stageFilter} onValueChange={(v) => { setStageFilter(v); setPagination(p => ({ ...p, page: 1 })); }}>
                <SelectTrigger className="w-[220px]" data-testid="stage-filter">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Filter by stage" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Stages</SelectItem>
                  <SelectItem value="signup_incomplete">1. Signup Incomplete</SelectItem>
                  <SelectItem value="account_password">2. Account with Password</SelectItem>
                  <SelectItem value="store_identity">3. Store Identity</SelectItem>
                  <SelectItem value="business_info">4. Business Info</SelectItem>
                  <SelectItem value="bank_details">5. Bank Details</SelectItem>
                  <SelectItem value="plan_selection">6. Plan Selection</SelectItem>
                  <SelectItem value="kyc_pending">7. KYC Pending</SelectItem>
                  <SelectItem value="kyc_completed">8. KYC Completed</SelectItem>
                  <SelectItem value="listing_complete">9. 1st Listing Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Vendor Table */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">
              Vendor List ({pagination.total_count})
              {stageFilter !== 'all' && (
                <Badge className={`ml-2 ${STAGE_CONFIG[stageFilter]?.color || ''}`}>
                  {STAGE_CONFIG[stageFilter]?.label}
                </Badge>
              )}
            </CardTitle>
            <CardDescription>Click stage cards above to filter. Click contact info to copy.</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : vendors.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-40 text-muted-foreground">
                <Users className="h-10 w-10 mb-2 opacity-30" />
                <p className="text-sm">No vendors found for this stage</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left py-3 px-4 font-medium">Vendor</th>
                      <th className="text-left py-3 px-4 font-medium">Contact</th>
                      <th className="text-left py-3 px-4 font-medium">Signup</th>
                      <th className="text-center py-3 px-4 font-medium">Current Stage</th>
                      <th className="text-center py-3 px-4 font-medium">Progress</th>
                      <th className="text-center py-3 px-4 font-medium">Joined</th>
                      <th className="text-center py-3 px-4 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vendors.map((vendor) => {
                      const stageCfg = getStageConfig(vendor.current_stage);
                      const StagIcon = stageCfg.icon;
                      return (
                        <tr key={vendor.vendor_id} className="border-b hover:bg-muted/30" data-testid={`vendor-row-${vendor.vendor_id}`}>
                          {/* Vendor Name */}
                          <td className="py-3 px-4">
                            <div className="font-medium">{vendor.name}</div>
                            <div className="text-xs text-muted-foreground font-mono">
                              {vendor.vendor_id?.slice(0, 8)}...
                            </div>
                          </td>

                          {/* Contact */}
                          <td className="py-3 px-4">
                            <div className="space-y-1">
                              {vendor.email && (
                                <button onClick={() => copyToClipboard(vendor.email)}
                                  className="flex items-center gap-1 text-blue-600 hover:underline text-xs" title="Click to copy">
                                  <Mail className="h-3 w-3" />{vendor.email}
                                </button>
                              )}
                              {vendor.phone && (
                                <a href={`tel:${vendor.phone}`} className="flex items-center gap-1 text-green-600 hover:underline text-xs">
                                  <Phone className="h-3 w-3" />{vendor.phone}
                                </a>
                              )}
                            </div>
                          </td>

                          {/* Signup Method */}
                          <td className="py-3 px-4">
                            {getSignupMethodBadge(vendor)}
                          </td>

                          {/* Stage Badge */}
                          <td className="py-3 px-4 text-center">
                            <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${stageCfg.color}`}>
                              <StagIcon className="h-3 w-3 flex-shrink-0" />
                              <span>{stageCfg.label}</span>
                            </div>
                            <div className="text-xs text-muted-foreground mt-0.5 max-w-[160px] mx-auto leading-tight">
                              {vendor.stage_details?.[0] || stageCfg.description}
                            </div>
                          </td>

                          {/* Progress */}
                          <td className="py-3 px-4">
                            <div className="w-24 mx-auto">
                              <Progress value={vendor.onboarding_progress || 0} className="h-2" />
                              <div className="text-xs text-center text-muted-foreground mt-1">
                                {vendor.onboarding_progress || 0}% (Step {stageCfg.step}/9)
                              </div>
                            </div>
                          </td>

                          {/* Joined */}
                          <td className="py-3 px-4 text-center text-xs text-muted-foreground">
                            {formatDate(vendor.joined_at)}
                          </td>

                          {/* Actions */}
                          <td className="py-3 px-4">
                            <div className="flex items-center justify-center gap-1">
                              <Button variant="ghost" size="sm"
                                onClick={() => copyToClipboard(`Name: ${vendor.name}\nEmail: ${vendor.email}\nPhone: ${vendor.phone}\nStage: ${stageCfg.label}`)}
                                title="Copy contact info" data-testid={`copy-contact-${vendor.vendor_id}`}>
                                <Copy className="h-4 w-4" />
                              </Button>

                              {crmStatus?.is_connected && (
                                <Button variant="ghost" size="sm"
                                  onClick={() => syncVendorToCrm(vendor.vendor_id)}
                                  disabled={syncingVendor === vendor.vendor_id}
                                  title={vendor.zoho_crm_id ? 'Update in Zoho CRM' : 'Sync to Zoho CRM'}
                                  className={vendor.zoho_crm_id ? 'text-green-600' : 'text-orange-600'}
                                  data-testid={`sync-crm-${vendor.vendor_id}`}>
                                  {syncingVendor === vendor.vendor_id
                                    ? <Loader2 className="h-4 w-4 animate-spin" />
                                    : vendor.zoho_crm_id
                                      ? <CheckCircle2 className="h-4 w-4" />
                                      : <Cloud className="h-4 w-4" />}
                                </Button>
                              )}

                              {vendor.zoho_crm_url ? (
                                <a href={vendor.zoho_crm_url} target="_blank" rel="noopener noreferrer" title="Open in Zoho CRM">
                                  <Button variant="ghost" size="sm" className="text-orange-600">
                                    <ExternalLink className="h-4 w-4" />
                                  </Button>
                                </a>
                              ) : (
                                <Button variant="ghost" size="sm" disabled className="text-muted-foreground">
                                  <ExternalLink className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>

          {/* Pagination */}
          {pagination.total_pages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <div className="text-sm text-muted-foreground">
                Page {pagination.page} of {pagination.total_pages} ({pagination.total_count} vendors)
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm"
                  onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
                  disabled={pagination.page <= 1} data-testid="prev-page">
                  <ChevronLeft className="h-4 w-4" />Previous
                </Button>
                <Button variant="outline" size="sm"
                  onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
                  disabled={pagination.page >= pagination.total_pages} data-testid="next-page">
                  Next<ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default VendorOnboardingFunnel;
